import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_86ba7c87-2c5f-4809-b4ca-291f6e117aa2/artifacts/wjmihcrt_black%20rento%20logo.png";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Still show success message for security (don't reveal if email exists)
      setSent(true);
      toast.success('If an account exists with that email, reset instructions have been sent.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#2a2a2a]">
        <div className="w-full max-w-md text-center space-y-6">
          <img src={LOGO_URL} alt="rentOcart.com" className="h-16 object-contain mx-auto mb-6" />
          <div className="bg-green-900/30 border border-green-700 p-6 rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-white">Check Your Email</h2>
            <p className="text-gray-300 mb-4">
              If an account exists for <strong className="text-cyan-400">{email}</strong>, we've sent password reset instructions.
            </p>
            <p className="text-sm text-gray-400">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
          </div>
          <Link to="/login">
            <Button variant="outline" className="w-full h-12 border-gray-700 text-white hover:bg-gray-800">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#2a2a2a]">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img src={LOGO_URL} alt="rentOcart.com" className="h-16 object-contain mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-2 text-white">Forgot Password?</h2>
          <p className="text-gray-400">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-gray-300">Email Address</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-14 text-lg pl-11 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500 focus:border-cyan-500"
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-cyan-500 via-green-500 to-orange-500 hover:from-cyan-600 hover:via-green-600 hover:to-orange-600"
            data-testid="send-reset-btn"
          >
            {loading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>

        <div className="text-center">
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 hover:underline inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
